import { cn } from "../shared/ui/index.ts";
import * as React from "react";
import type { Patient } from "../core/services/patients.service.ts";
import {
  formatFileDate,
  formatFileSize,
  getPatientFiles,
} from "../dashboard/patient-media-previews.ts";
import type {
  MediaFile,
  MediaFileType,
} from "../dashboard/patient-media.types.ts";
import { I } from "../shared/icons/icons.tsx";
import { OptionStepper } from "../shared/selection-nav.component.tsx";
import { Select } from "../shared/select.component.tsx";

const TYPE_META: Record<
  MediaFileType,
  { label: string; filterLabel: string; color: string; textColor: string }
> = {
  jpeg: {
    label: "JPEG",
    filterLabel: "Photos",
    color: "#dcfce7",
    textColor: "#15803d",
  },
  png: {
    label: "PNG",
    filterLabel: "Radios",
    color: "#1e293b",
    textColor: "#7ab8e8",
  },
  stl: {
    label: "STL",
    filterLabel: "3D",
    color: "#ede9fe",
    textColor: "#6d28d9",
  },
  mp4: {
    label: "MP4",
    filterLabel: "Vidéos",
    color: "#0f172a",
    textColor: "#22d3ee",
  },
  pdf: {
    label: "PDF",
    filterLabel: "PDF",
    color: "#fee2e2",
    textColor: "#dc2626",
  },
};

function hashId(id: string): number {
  return id.split("").reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 0);
}

// ── Thumbnails ──────────────────────────────────────────────────────────────
// Orthodontic clinical imagery — no face drawings, dental/radiographic content only

// Dessine une dent (couronne + racine) comme sur une radio
function toothShape(
  x: number,
  y: number,
  w: number,
  crownH: number,
  rootH: number,
  fill: string,
  opacity = 1,
): string {
  const cx = x + w / 2;
  return `<path d="M ${x} ${y} Q ${x} ${y - 2} ${cx} ${y - crownH * 0.08} Q ${x + w} ${y - 2} ${x + w} ${y}
           L ${x + w} ${y + crownH} Q ${x + w} ${y + crownH + 3} ${cx + w * 0.15} ${y + crownH + rootH}
           Q ${cx} ${y + crownH + rootH + 4} ${cx - w * 0.15} ${y + crownH + rootH}
           Q ${x} ${y + crownH + 3} ${x} ${y + crownH} Z"
    fill="${fill}" opacity="${opacity}" stroke="${fill === "#f0ece0" ? "#c8b890" : "none"}" stroke-width="0.5"/>`;
}

// Bracket orthodontique SVG
function bracket(cx: number, y: number): string {
  return `<rect x="${cx - 4}" y="${y}" width="8" height="5" rx="0.8" fill="#8090b0" opacity="0.9"/>
          <rect x="${cx - 4.5}" y="${y + 1.5}" width="9" height="2" rx="0.4" fill="#9aaac0"/>
          <rect x="${cx - 1.2}" y="${y - 1}" width="2.4" height="2" fill="#8090b0" opacity="0.8"/>
          <rect x="${cx - 1.2}" y="${y + 5}" width="2.4" height="2" fill="#8090b0" opacity="0.8"/>`;
}

function jpegThumb(id: string): string {
  const isOcclusal = id.includes("Occlusal");
  const isFrontal =
    id.includes("Frontal") ||
    id.includes("Face") ||
    (!id.includes("Profil") &&
      !id.includes("Intrao") &&
      !isOcclusal &&
      !id.includes("Lateral"));

  // Toutes les photos sont intra- ou extra-buccales cliniques — ZÉRO dessin de visage

  if (isOcclusal) {
    // Vue occlusale — arcade vue du dessus avec brackets et fil
    const n = 7;
    const teeth = [...Array(n)]
      .map((_, i) => {
        const t = (i / (n - 1)) * Math.PI;
        const cx = 80 - Math.cos(t - Math.PI / 2) * 48;
        const cy = 62 + Math.sin(Math.abs(t - Math.PI / 2)) * 18;
        const rw = i === 0 || i === n - 1 ? 7 : i === 1 || i === n - 2 ? 8 : 10;
        return `<ellipse cx="${cx}" cy="${cy}" rx="${rw}" ry="7" fill="#f4f0e8" stroke="#c8b890" stroke-width="0.7"/>
              <ellipse cx="${cx}" cy="${cy}" rx="${rw * 0.55}" ry="3.5" fill="#e8e0d0" opacity="0.6"/>`;
      })
      .join("");
    const arcWire = [...Array(20)]
      .map((_, i) => {
        const t = (i / 19) * Math.PI;
        const x = 80 - Math.cos(t - Math.PI / 2) * 48;
        const y = 62 + Math.sin(Math.abs(t - Math.PI / 2)) * 18;
        return `${i === 0 ? "M" : "L"} ${x} ${y}`;
      })
      .join(" ");
    return `<svg viewBox="0 0 160 120" xmlns="http://www.w3.org/2000/svg" width="160" height="120">
      <rect width="160" height="120" fill="#c8e0d8"/>
      <ellipse cx="80" cy="65" rx="60" ry="40" fill="#e8c8b0" opacity="0.7"/>
      <ellipse cx="80" cy="50" rx="54" ry="18" fill="#d4a888" opacity="0.5"/>
      ${teeth}
      <path d="${arcWire}" fill="none" stroke="#b0b8c8" stroke-width="1.8" opacity="0.7"/>
      <rect x="0" y="104" width="160" height="16" fill="rgba(0,0,0,0.55)"/>
      <text x="80" y="115" font-size="7.5" fill="#ddd" text-anchor="middle" font-family="monospace">Vue occlusale maxillaire</text>
    </svg>`;
  }

  if (isFrontal) {
    // Sourire frontal — 6 dents supérieures + 6 inférieures avec brackets MBT
    const topTeeth = [...Array(6)]
      .map((_, i) => {
        const x = 20 + i * 20;
        const w = i === 0 || i === 5 ? 14 : i === 2 || i === 3 ? 19 : 16;
        return (
          toothShape(x + (20 - w) / 2, 42, w, 22, 0, "#f4f0e8") +
          bracket(x + 10, 47)
        );
      })
      .join("");
    const botTeeth = [...Array(6)]
      .map((_, i) => {
        const x = 22 + i * 19;
        const w = i === 0 || i === 5 ? 12 : i === 2 || i === 3 ? 16 : 14;
        return toothShape(x, 68, w, 18, 0, "#f0ece4");
      })
      .join("");
    const archwire = `<path d="M 28 49 Q 80 46 132 49" fill="none" stroke="#c0c8d8" stroke-width="1.5" opacity="0.8"/>`;
    return `<svg viewBox="0 0 160 120" xmlns="http://www.w3.org/2000/svg" width="160" height="120">
      <rect width="160" height="120" fill="#1a0e08"/>
      <rect x="8" y="30" width="144" height="60" rx="6" fill="#c07850" opacity="0.7"/>
      <ellipse cx="80" cy="52" rx="68" ry="12" fill="#e8c0a0" opacity="0.6"/>
      <ellipse cx="80" cy="68" rx="64" ry="10" fill="#d4a880" opacity="0.5"/>
      ${topTeeth}${archwire}${botTeeth}
      <rect x="0" y="104" width="160" height="16" fill="rgba(0,0,0,0.6)"/>
      <text x="80" y="115" font-size="7.5" fill="#ddd" text-anchor="middle" font-family="monospace">Vue frontale · brackets MBT</text>
    </svg>`;
  }

  // Latérale / profil intra-buccal
  const n = 5;
  const topTeeth = [...Array(n)]
    .map((_, i) => {
      const x = 18 + i * 24;
      const w = i === 0 ? 12 : i === 1 ? 10 : 13;
      const crownH = i === 0 ? 20 : 16 + i * 2;
      return (
        toothShape(x, 38, w, crownH, 0, "#f4f0e8") + bracket(x + w / 2, 42)
      );
    })
    .join("");
  const botTeeth = [...Array(n)]
    .map((_, i) => {
      const x = 20 + i * 22;
      const w = i === 0 ? 11 : 12;
      return toothShape(x, 66, w, 14, 0, "#f0ece4");
    })
    .join("");
  const wire = `<path d="M 24 44 Q 80 42 ${18 + (n - 1) * 24 + 6} 44" fill="none" stroke="#c0c8d8" stroke-width="1.5" opacity="0.8"/>`;
  return `<svg viewBox="0 0 160 120" xmlns="http://www.w3.org/2000/svg" width="160" height="120">
    <rect width="160" height="120" fill="#1a0e08"/>
    <rect x="6" y="28" width="148" height="64" rx="5" fill="#b87850" opacity="0.65"/>
    ${topTeeth}${wire}${botTeeth}
    <rect x="0" y="104" width="160" height="16" fill="rgba(0,0,0,0.6)"/>
    <text x="80" y="115" font-size="7.5" fill="#ddd" text-anchor="middle" font-family="monospace">Vue latérale droite</text>
  </svg>`;
}

function pngThumb(id: string): string {
  const isPanoramic = id.includes("pan") || id.includes("Pan");
  const label = isPanoramic ? "PANORAMIQUE DENTAIRE" : "TÉLÉRADIOGRAPHIE";

  if (isPanoramic) {
    // OPG réaliste — dents courbées sur arcade, os mandibulaire, condyles, sinus
    const teethTop = [...Array(14)]
      .map((_, i) => {
        const t = (i / 13) * Math.PI;
        const bx = 80 - Math.cos(t - Math.PI / 2) * 62;
        const by = 58;
        const crownH = i < 3 || i > 10 ? 14 : i < 5 || i > 8 ? 16 : 18;
        const rootH = i < 3 || i > 10 ? 12 : i < 5 || i > 8 ? 18 : 22;
        const w = i < 3 || i > 10 ? 6 : i < 5 || i > 8 ? 7 : 8;
        return toothShape(
          bx - w / 2,
          by - crownH,
          w,
          crownH,
          rootH,
          "#9ac0e0",
          0.8,
        );
      })
      .join("");
    const teethBot = [...Array(14)]
      .map((_, i) => {
        const t = (i / 13) * Math.PI;
        const bx = 80 - Math.cos(t - Math.PI / 2) * 56;
        const by = 62;
        const crownH = i < 3 || i > 10 ? 12 : i < 5 || i > 8 ? 14 : 16;
        const rootH = i < 3 || i > 10 ? 14 : i < 5 || i > 8 ? 20 : 26;
        const w = i < 3 || i > 10 ? 6 : i < 5 || i > 8 ? 7 : 8;
        return toothShape(bx - w / 2, by, w, crownH, rootH, "#80aace", 0.75);
      })
      .join("");
    const bone = `<path d="M 8 90 Q 30 105 80 108 Q 130 105 152 90 L 152 70 Q 130 82 80 85 Q 30 82 8 70 Z" fill="#3a6090" opacity="0.18"/>`;
    const maxSinus = `<ellipse cx="42" cy="38" rx="20" ry="14" fill="none" stroke="#5090b8" stroke-width="0.6" opacity="0.4"/>
                      <ellipse cx="118" cy="38" rx="20" ry="14" fill="none" stroke="#5090b8" stroke-width="0.6" opacity="0.4"/>`;
    const condyles = `<ellipse cx="10" cy="30" rx="7" ry="12" fill="#4a7090" opacity="0.35"/>
                      <ellipse cx="150" cy="30" rx="7" ry="12" fill="#4a7090" opacity="0.35"/>`;
    const canal = `<path d="M 18 88 Q 50 96 80 97 Q 110 96 142 88" fill="none" stroke="#3a7898" stroke-width="1" stroke-dasharray="3,2" opacity="0.5"/>`;
    return `<svg viewBox="0 0 160 120" xmlns="http://www.w3.org/2000/svg" width="160" height="120">
      <rect width="160" height="120" fill="#030810"/>
      ${bone}${condyles}${maxSinus}${canal}
      ${teethTop}${teethBot}
      <rect x="0" y="102" width="160" height="18" fill="#020508"/>
      <text x="80" y="113" font-size="6.5" fill="#4a88b0" text-anchor="middle" font-family="monospace" letter-spacing="0.5">${label} · 2024-03-15</text>
    </svg>`;
  }

  // Téléradiographie — profil crânien + tracé céphalométrique points Ricketts
  const skull = `M 118 8 Q 148 12 152 38 Q 156 64 144 82 Q 130 100 108 104 Q 88 108 70 98 Q 52 88 50 68 Q 48 50 58 35 Q 68 20 118 8 Z`;
  const mandible = `M 70 98 Q 60 108 55 114 L 72 116 Q 82 112 92 108 Q 100 104 108 104`;
  const cervical = [...Array(4)]
    .map(
      (_, i) =>
        `<rect x="24" y="${60 + i * 13}" width="22" height="11" rx="2" fill="#4a7090" opacity="0.3"/>`,
    )
    .join("");
  // Points céphalométriques de Ricketts
  const cephPoints = [
    { cx: 118, cy: 10, label: "N" },
    { cx: 88, cy: 38, label: "A" },
    { cx: 80, cy: 54, label: "B" },
    { cx: 78, cy: 100, label: "Pg" },
    { cx: 120, cy: 8, label: "Na" },
  ];
  const pts = cephPoints
    .map(
      (p) =>
        `<circle cx="${p.cx}" cy="${p.cy}" r="2" fill="#f59e0b" opacity="0.9"/>
     <text x="${p.cx + 3}" y="${p.cy + 3}" font-size="5" fill="#f59e0b" font-family="monospace" opacity="0.8">${p.label}</text>`,
    )
    .join("");
  const tracingLine = `<polyline points="118,10 88,38 80,54 78,100" fill="none" stroke="#f59e0b" stroke-width="0.8" stroke-dasharray="3,2" opacity="0.6"/>`;
  const fhPlane = `<line x1="48" y1="45" x2="152" y2="30" stroke="#22d3ee" stroke-width="0.7" stroke-dasharray="4,3" opacity="0.5"/>`;
  const occlPlane = `<line x1="60" y1="72" x2="130" y2="78" stroke="#22d3ee" stroke-width="0.7" stroke-dasharray="4,3" opacity="0.4"/>`;
  const frontTeeth = [...Array(4)]
    .map(
      (_, i) =>
        `<rect x="${82 + i * 8}" y="92" width="6" height="${12 - i}" rx="1.5" fill="#7aace8" opacity="0.7"/>`,
    )
    .join("");
  return `<svg viewBox="0 0 160 120" xmlns="http://www.w3.org/2000/svg" width="160" height="120">
    <rect width="160" height="120" fill="#030810"/>
    <path d="${skull}" fill="#3060a0" opacity="0.2" stroke="#5088c0" stroke-width="0.8"/>
    <path d="${mandible}" fill="none" stroke="#5088c0" stroke-width="0.7" opacity="0.5"/>
    ${cervical}${frontTeeth}${fhPlane}${occlPlane}${tracingLine}${pts}
    <rect x="0" y="102" width="160" height="18" fill="#020508"/>
    <text x="80" y="113" font-size="6.5" fill="#4a88b0" text-anchor="middle" font-family="monospace" letter-spacing="0.5">${label} · 2024-03-15</text>
  </svg>`;
}

function stlThumb(id: string): string {
  const isMaxi =
    id.includes("maxi") || id.includes("Maxi") || id.includes("max");
  const isSuperposition = id.includes("Super") || id.includes("super");
  const label = isSuperposition
    ? "SUPERPOSITION 3D"
    : isMaxi
      ? "ARCADE MAXILLAIRE"
      : "ARCADE MANDIBULAIRE";
  const h = hashId(id);
  const arcColor = isSuperposition ? "#22d3ee" : isMaxi ? "#c4b5fd" : "#86efac";
  const gumFill = isSuperposition
    ? "rgba(34,211,238,0.10)"
    : isMaxi
      ? "rgba(196,181,253,0.15)"
      : "rgba(134,239,172,0.15)";
  const bgColor = isSuperposition ? "#060d18" : "#0a0818";

  const grid = [...Array(6)]
    .map(
      (_, i) =>
        `<line x1="${14 + i * 27}" y1="8" x2="${14 + i * 27}" y2="112" stroke="#1a1640" stroke-width="0.4"/>
     <line x1="8" y1="${12 + i * 18}" x2="152" y2="${12 + i * 18}" stroke="#1a1640" stroke-width="0.4"/>`,
    )
    .join("");

  const numTeeth = isMaxi ? 14 : 12;
  // Dents sur l'arcade (forme réaliste)
  const archTeeth = [...Array(numTeeth)]
    .map((_, i) => {
      const t = (i / (numTeeth - 1)) * Math.PI;
      const cx = 80 - Math.cos(t - Math.PI / 2) * 52;
      const cy = 64 + Math.sin(Math.abs(t - Math.PI / 2)) * 16 - 6;
      const rw =
        i < 2 || i > numTeeth - 3 ? 4.5 : i < 4 || i > numTeeth - 5 ? 6 : 7.5;
      const rh = 5 + ((h + i * 7) % 3);
      const alpha = 0.6 + (i % 3) * 0.12;
      // Effet 3D : face occlusale + highlight
      return `<ellipse cx="${cx}" cy="${cy}" rx="${rw}" ry="${rh}" fill="${arcColor}" opacity="${alpha}"/>
            <ellipse cx="${cx}" cy="${cy - rh * 0.35}" rx="${rw * 0.55}" ry="${rh * 0.35}" fill="rgba(255,255,255,0.18)"/>
            <ellipse cx="${cx}" cy="${cy}" rx="${rw * 0.4}" ry="${rh * 0.3}" fill="rgba(255,255,255,0.06)"/>`;
    })
    .join("");

  // Gencive en arc
  const gumPts = [...Array(20)]
    .map((_, i) => {
      const t = (i / 19) * Math.PI;
      return `${i === 0 ? "M" : "L"} ${80 - Math.cos(t - Math.PI / 2) * 60} ${68 + Math.sin(Math.abs(t - Math.PI / 2)) * 22 - 8}`;
    })
    .join(" ");

  // Superposition : deuxième arcade en transparence
  const overlayTeeth = isSuperposition
    ? [...Array(numTeeth)]
        .map((_, i) => {
          const t = (i / (numTeeth - 1)) * Math.PI;
          const cx = 80 - Math.cos(t - Math.PI / 2) * 48;
          const cy = 68 + Math.sin(Math.abs(t - Math.PI / 2)) * 14;
          return `<ellipse cx="${cx}" cy="${cy}" rx="5" ry="4" fill="rgba(255,200,50,0.25)" stroke="rgba(255,200,50,0.5)" stroke-width="0.5"/>`;
        })
        .join("")
    : "";

  return `<svg viewBox="0 0 160 120" xmlns="http://www.w3.org/2000/svg" width="160" height="120">
    <rect width="160" height="120" fill="${bgColor}"/>
    ${grid}
    <path d="${gumPts}" fill="${gumFill}" stroke="${arcColor}" stroke-width="1" opacity="0.5"/>
    ${archTeeth}${overlayTeeth}
    <rect x="0" y="100" width="160" height="20" fill="rgba(0,0,0,0.65)"/>
    <text x="80" y="113" font-size="7.5" fill="${arcColor}" text-anchor="middle" font-family="monospace" opacity="0.95">${label}</text>
  </svg>`;
}

function mp4Thumb(id: string): string {
  const h = hashId(id);
  const progressW = 28 + (h % 40);
  const label = h % 2 === 0 ? "Évolution · 6 mois" : "Technique bracket MBT";
  return `<svg viewBox="0 0 160 120" xmlns="http://www.w3.org/2000/svg" width="160" height="120">
    <rect width="160" height="120" fill="#08121e"/>
    <!-- Avant / Après -->
    <rect x="6" y="14" width="68" height="76" rx="3" fill="#0a1e30" stroke="#1a4060" stroke-width="0.7"/>
    <rect x="86" y="14" width="68" height="76" rx="3" fill="#0a1e30" stroke="#1a4060" stroke-width="0.7"/>
    <!-- Contenu avant : dents non traitées -->
    ${[...Array(6)]
      .map((_, i) => {
        const x = 10 + i * 10;
        const w = 7;
        const y = 52;
        return `<rect x="${x}" y="${y}" width="${w}" height="${14 + (i === 0 || i === 5 ? -2 : 0)}" rx="2" fill="#d8d0b8" opacity="0.7"/>`;
      })
      .join("")}
    <!-- Contenu après : dents avec brackets -->
    ${[...Array(6)]
      .map((_, i) => {
        const x = 90 + i * 10;
        const w = 7;
        const y = 52;
        return `<rect x="${x}" y="${y}" width="${w}" height="14" rx="2" fill="#f0ecd8" opacity="0.8"/>
              <rect x="${x + 1}" y="${y + 4}" width="5" height="4" rx="0.5" fill="#8090b0" opacity="0.8"/>`;
      })
      .join("")}
    <path d="M 90 56 L 144 56" stroke="#c0c8d8" stroke-width="1.2" opacity="0.7"/>
    <text x="40" y="36" font-size="7" fill="#7ab8e0" text-anchor="middle" font-family="monospace">Avant</text>
    <text x="120" y="36" font-size="7" fill="#22d3ee" text-anchor="middle" font-family="monospace">Après</text>
    <line x1="80" y1="14" x2="80" y2="90" stroke="#22d3ee" stroke-width="0.7" stroke-dasharray="3,2" opacity="0.5"/>
    <!-- Bouton play centré -->
    <circle cx="80" cy="52" r="11" fill="rgba(255,255,255,0.1)"/>
    <polygon points="76,46 76,58 88,52" fill="white" opacity="0.8"/>
    <!-- Barre de progression -->
    <rect x="6" y="96" width="148" height="3" rx="1.5" fill="rgba(255,255,255,0.1)"/>
    <rect x="6" y="96" width="${progressW}" height="3" rx="1.5" fill="#22d3ee"/>
    <rect x="0" y="104" width="160" height="16" fill="rgba(0,0,0,0.5)"/>
    <text x="80" y="115" font-size="7.5" fill="#22d3ee" text-anchor="middle" font-family="sans-serif">${label}</text>
  </svg>`;
}

function pdfThumb(name: string): string {
  const shortName = name.replace(/-/g, " ").slice(0, 22);
  const isConsent = name.toLowerCase().includes("consent");
  const isDevis = name.toLowerCase().includes("devis");
  const accentColor = isConsent ? "#7c3aed" : isDevis ? "#0369a1" : "#dc2626";
  const accentDark = isConsent ? "#5b21b6" : isDevis ? "#075985" : "#991b1b";
  const typeLabel = isConsent ? "CONSENTEMENT" : isDevis ? "DEVIS" : "RAPPORT";
  const lines = [0.9, 0.7, 0.85, 0.6, 0.78, 0.45]
    .map(
      (w, i) =>
        `<rect x="10" y="${52 + i * 9}" width="${Math.round(w * 140)}" height="4" rx="2" fill="#e2e8f0"/>`,
    )
    .join("");
  return `<svg viewBox="0 0 160 120" xmlns="http://www.w3.org/2000/svg" width="160" height="120">
    <rect width="160" height="120" fill="#f8fafc"/>
    <rect x="0" y="0" width="160" height="34" fill="${accentColor}"/>
    <rect x="0" y="30" width="160" height="5" fill="${accentDark}"/>
    <text x="12" y="14" font-size="11" font-weight="bold" fill="white" font-family="sans-serif">PDF</text>
    <text x="12" y="27" font-size="8" fill="rgba(255,255,255,0.75)" font-family="monospace">${typeLabel}</text>
    <text x="10" y="48" font-size="8.5" font-weight="bold" fill="#1e293b" font-family="sans-serif">${shortName}</text>
    ${lines}
    <rect x="0" y="105" width="160" height="15" fill="#f1f5f9"/>
    <text x="148" y="115" font-size="6.5" fill="#94a3b8" text-anchor="end" font-family="sans-serif">Cabinet Ligam</text>
  </svg>`;
}

function getThumbSvg(file: MediaFile): string {
  switch (file.type) {
    case "jpeg":
      return jpegThumb(file.id);
    case "png":
      return pngThumb(file.id);
    case "stl":
      return stlThumb(file.id);
    case "mp4":
      return mp4Thumb(file.id);
    case "pdf":
      return pdfThumb(file.name);
  }
}

// ── Thumbnail component ──────────────────────────────────────────────────────

function FileThumbnail({
  file,
  selected,
  focused,
  onPointerDown,
  onDoubleClick,
}: {
  file: MediaFile;
  selected: boolean;
  focused: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
  onDoubleClick: () => void;
}) {
  const meta = TYPE_META[file.type];
  const svg = getThumbSvg(file);
  const dataUri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

  return (
    <div
      role="option"
      aria-selected={selected}
      tabIndex={focused ? 0 : -1}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-lg border-2 bg-white text-left transition-all cursor-pointer select-none",
        selected
          ? "border-sf-primary bg-sf-primary-50/30 shadow-sm"
          : focused
            ? "border-sf-primary/50"
            : "border-sf-border hover:border-sf-primary/40 hover:shadow-sm",
      )}
    >
      {/* Checkbox overlay en haut-gauche au hover ou si sélectionné */}
      <div
        className={cn(
          "absolute left-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all",
          selected
            ? "border-sf-primary bg-sf-primary text-white opacity-100"
            : "border-white bg-black/20 text-transparent opacity-0 group-hover:opacity-100",
        )}
      >
        <I.Check size={10} />
      </div>

      <div
        className="w-full shrink-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url("${dataUri}")`, aspectRatio: "4/3" }}
      />
      <div className="flex min-w-0 items-center gap-1 px-1.5 py-1">
        <span
          className="shrink-0 rounded px-1 py-0.5 text-[9px] font-bold uppercase leading-none"
          style={{ background: meta.color, color: meta.textColor }}
        >
          {meta.label}
        </span>
        <span className="min-w-0 truncate text-[10px] text-sf-text-muted">
          {file.name}
        </span>
      </div>
    </div>
  );
}

// ── Preview components (unchanged) ──────────────────────────────────────────

function PhotoPreview({ fileId }: { fileId: string }) {
  const isOcclusal = fileId.includes("Occlusal");
  const isLateral = fileId.includes("Lateral") || fileId.includes("Profil");

  // Aperçu agrandi de la photo clinique — même logique que la vignette mais en grande
  const numTopTeeth = isOcclusal ? 7 : 6;
  const numBotTeeth = isOcclusal ? 0 : 6;

  return (
    <div
      className="flex h-full items-center justify-center"
      style={{ background: "#111" }}
    >
      <div
        className="relative overflow-hidden rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.5)]"
        style={{ width: 360, height: 260, maxHeight: "85%", maxWidth: "95%" }}
      >
        <svg
          viewBox="0 0 360 260"
          xmlns="http://www.w3.org/2000/svg"
          width="360"
          height="260"
          style={{ display: "block" }}
        >
          <rect width="360" height="260" fill="#150e06" />
          {/* Contexte buccal */}
          <ellipse
            cx="180"
            cy={isOcclusal ? 150 : 140}
            rx="155"
            ry={isOcclusal ? 95 : 75}
            fill="#c07850"
            opacity="0.6"
          />
          <ellipse
            cx="180"
            cy={isOcclusal ? 108 : 108}
            rx="140"
            ry="42"
            fill="#e0b890"
            opacity="0.55"
          />
          {isOcclusal && (
            <ellipse
              cx="180"
              cy={182}
              rx="138"
              ry="40"
              fill="#d0a880"
              opacity="0.45"
            />
          )}

          {/* Dents supérieures */}
          {[...Array(numTopTeeth)].map((_, i) => {
            const span = isOcclusal ? 42 : 46;
            const startX = 180 - (numTopTeeth / 2 - 0.5) * span;
            const x = startX + i * span;
            const w = isOcclusal
              ? 32 - Math.abs(i - 3) * 3
              : i === 0 || i === 5
                ? 32
                : i === 2 || i === 3
                  ? 44
                  : 36;
            const h = isOcclusal
              ? 28 + (i === 0 || i === numTopTeeth - 1 ? -2 : 4)
              : 40 + (i === 2 || i === 3 ? 8 : 0);
            const ty = isOcclusal ? 90 : 88;
            return (
              <g key={`t${i}`}>
                <rect
                  x={x - w / 2}
                  y={ty}
                  width={w}
                  height={h}
                  rx={7}
                  fill="#f4f0e8"
                  stroke="#c8b890"
                  strokeWidth={1}
                />
                <rect
                  x={x - w / 2 + 2}
                  y={ty}
                  width={w - 4}
                  height={8}
                  rx={3}
                  fill="#e8e0d0"
                  opacity={0.6}
                />
                {/* Bracket */}
                {!isOcclusal && (
                  <g>
                    <rect
                      x={x - 10}
                      y={ty + 14}
                      width={20}
                      height={12}
                      rx={2}
                      fill="#8090b0"
                      opacity={0.85}
                    />
                    <rect
                      x={x - 11}
                      y={ty + 17}
                      width={22}
                      height={5}
                      rx={1}
                      fill="#9aaac0"
                    />
                    <rect
                      x={x - 3}
                      y={ty + 10}
                      width={6}
                      height={5}
                      fill="#8090b0"
                      opacity={0.8}
                    />
                    <rect
                      x={x - 3}
                      y={ty + 26}
                      width={6}
                      height={5}
                      fill="#8090b0"
                      opacity={0.8}
                    />
                  </g>
                )}
              </g>
            );
          })}

          {/* Fil orthodontique */}
          {!isOcclusal && (
            <path
              d={`M ${180 - (numTopTeeth / 2 - 0.5) * 46} 120 Q 180 116 ${180 + (numTopTeeth / 2 - 0.5) * 46} 120`}
              fill="none"
              stroke="#c0c8d8"
              strokeWidth={3}
              opacity={0.75}
            />
          )}

          {/* Dents inférieures */}
          {!isOcclusal &&
            [...Array(numBotTeeth)].map((_, i) => {
              const startX = 180 - (numBotTeeth / 2 - 0.5) * 44;
              const x = startX + i * 44;
              const w = i === 0 || i === 5 ? 28 : i === 2 || i === 3 ? 38 : 32;
              return (
                <rect
                  key={`b${i}`}
                  x={x - w / 2}
                  y={152}
                  width={w}
                  height={34}
                  rx={6}
                  fill="#ece8e0"
                  stroke="#c0b888"
                  strokeWidth={1}
                />
              );
            })}

          {/* Légende */}
          <rect x={0} y={220} width={360} height={40} fill="rgba(0,0,0,0.65)" />
          <text
            x={180}
            y={244}
            fontSize={13}
            fill="#e0e0e0"
            textAnchor="middle"
            fontFamily="monospace"
          >
            {isOcclusal
              ? "Vue occlusale — Arcade maxillaire"
              : isLateral
                ? "Vue latérale droite"
                : "Vue frontale — Brackets MBT"}
          </text>
        </svg>
      </div>
    </div>
  );
}

function XrayPreview({ fileId }: { fileId: string }) {
  const isPanoramic = fileId.includes("pan") || fileId.includes("Pan");
  const numTeeth = isPanoramic ? 14 : 8;
  const spacing = isPanoramic ? 22 : 28;
  const startX = isPanoramic ? 8 : 14;

  return (
    <div
      className="flex h-full items-center justify-center"
      style={{ background: "#04080f" }}
    >
      <div
        className="overflow-hidden rounded-xl shadow-[0_0_60px_rgba(0,100,200,0.2)]"
        style={{ width: isPanoramic ? 480 : 320, maxWidth: "95%" }}
      >
        <svg
          viewBox={`0 0 ${isPanoramic ? 480 : 320} 240`}
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          style={{ display: "block" }}
        >
          <rect width="100%" height="240" fill="#04080f" />
          {isPanoramic && (
            <>
              <path
                d="M 24 120 Q 60 145 120 150 Q 240 155 360 150 Q 420 145 456 120"
                fill="none"
                stroke="#3a6890"
                strokeWidth="1.5"
                opacity="0.4"
              />
              <path
                d="M 18 50 Q 60 35 120 32 Q 240 28 360 32 Q 420 35 462 50"
                fill="none"
                stroke="#3a6890"
                strokeWidth="1"
                opacity="0.3"
              />
              <ellipse
                cx="16"
                cy="40"
                rx="10"
                ry="15"
                fill="#3a6090"
                opacity="0.25"
              />
              <ellipse
                cx="464"
                cy="40"
                rx="10"
                ry="15"
                fill="#3a6090"
                opacity="0.25"
              />
            </>
          )}
          <g opacity="0.82">
            {[...Array(numTeeth)].map((_, i) => {
              const x = startX + i * spacing + (i % 3);
              const toothH = 38 + (i % 4) * 12;
              return (
                <g key={i}>
                  <rect
                    x={x}
                    y={90 - toothH / 2}
                    width={spacing - 3}
                    height={toothH}
                    rx={8}
                    fill="#7ab8e8"
                    opacity={0.65}
                  />
                  <ellipse
                    cx={x + (spacing - 3) / 2}
                    cy={90 - toothH / 2 + 7}
                    rx={(spacing - 5) / 2}
                    ry={5}
                    fill="#a0d0f8"
                    opacity={0.55}
                  />
                  <ellipse
                    cx={x + (spacing - 3) / 2}
                    cy={90 + toothH / 2 - 7}
                    rx={(spacing - 5) / 2}
                    ry={5}
                    fill="#a0d0f8"
                    opacity={0.55}
                  />
                  <rect
                    x={x + 3}
                    y={82}
                    width={spacing - 9}
                    height={16}
                    rx={4}
                    fill="#3a6a9a"
                    opacity={0.4}
                  />
                </g>
              );
            })}
          </g>
          <rect x={0} y={190} width="100%" height={50} fill="#020508" />
          <text
            x="50%"
            y={215}
            fontSize={11}
            fill="#4a8ab0"
            textAnchor="middle"
            fontFamily="monospace"
          >
            {isPanoramic
              ? "PANORAMIQUE DENTAIRE · 2024-03-15"
              : "TÉLÉRADIOGRAPHIE · 2024-03-15"}
          </text>
        </svg>
      </div>
    </div>
  );
}

function StlPreview({ fileId }: { fileId: string }) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isMaxi =
    fileId.includes("maxi") ||
    fileId.includes("Maxi") ||
    fileId.includes("max");
  const isSuperposition = fileId.includes("Super") || fileId.includes("super");

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let animId: number;
    let cleanup: (() => void) | null = null;

    import("three").then((THREE) => {
      if (!container) return;
      const w = container.clientWidth || 400;
      const h = container.clientHeight || 300;
      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
      });
      renderer.setSize(w, h);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setClearColor(0x0d0a20, 1);
      container.appendChild(renderer.domElement);
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(42, w / h, 0.1, 100);
      camera.position.set(0, 2.2, 5.5);
      camera.lookAt(0, 0.3, 0);
      scene.add(new THREE.AmbientLight(0xffffff, 0.45));
      const d1 = new THREE.DirectionalLight(0xffffff, 0.9);
      d1.position.set(4, 6, 4);
      scene.add(d1);
      const d2 = new THREE.DirectionalLight(0x8080ff, 0.35);
      d2.position.set(-4, 2, -3);
      scene.add(d2);
      const group = new THREE.Group();
      const gumGeo = new THREE.CylinderGeometry(
        2.1,
        2.1,
        0.28,
        32,
        1,
        false,
        Math.PI * 0.05,
        Math.PI * 0.9,
      );
      const gumMat = new THREE.MeshPhongMaterial({
        color: 0xd06070,
        shininess: 35,
      });
      const gumMesh = new THREE.Mesh(gumGeo, gumMat);
      gumMesh.position.y = -0.14;
      group.add(gumMesh);
      const numTeeth = isMaxi ? 14 : 12;
      const toothMat = new THREE.MeshPhongMaterial({
        color: 0xf2ede0,
        shininess: 90,
        specular: 0x888888,
      });
      const hashVal = fileId
        .split("")
        .reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 0);
      for (let i = 0; i < numTeeth; i++) {
        const t = (i / (numTeeth - 1)) * Math.PI;
        const cx = -Math.cos(t - Math.PI / 2) * 1.85;
        const cz = Math.sin(Math.abs(t - Math.PI / 2)) * 0.65 - 0.2;
        const toothH = 0.42 + ((hashVal + i * 7) % 5) * 0.07;
        const toothW =
          i < 2 || i > numTeeth - 3
            ? 0.16
            : i < 4 || i > numTeeth - 5
              ? 0.22
              : 0.27;
        const toothGeo = new THREE.CylinderGeometry(
          toothW / 2,
          (toothW / 2) * 0.9,
          toothH,
          8,
        );
        const tooth = new THREE.Mesh(toothGeo, toothMat);
        tooth.position.set(cx, toothH / 2 + 0.06, cz);
        tooth.rotation.y = -(t - Math.PI / 2) * 0.4;
        group.add(tooth);
      }
      if (isSuperposition) {
        const overlayMat = new THREE.MeshPhongMaterial({
          color: 0x22d3ee,
          transparent: true,
          opacity: 0.35,
          wireframe: false,
        });
        const overlayGeo = new THREE.CylinderGeometry(
          2.0,
          2.0,
          0.15,
          32,
          1,
          false,
          Math.PI * 0.05,
          Math.PI * 0.9,
        );
        const overlay = new THREE.Mesh(overlayGeo, overlayMat);
        overlay.position.y = 0.2;
        group.add(overlay);
      }
      scene.add(group);
      group.rotation.x = -0.18;
      let angle = 0;
      const animate = () => {
        animId = requestAnimationFrame(animate);
        angle += 0.007;
        group.rotation.y = angle;
        renderer.render(scene, camera);
      };
      animate();
      cleanup = () => {
        cancelAnimationFrame(animId);
        renderer.dispose();
        if (container.contains(renderer.domElement))
          container.removeChild(renderer.domElement);
      };
    });
    return () => {
      cleanup?.();
    };
  }, [fileId, isMaxi, isSuperposition]);

  return (
    <div
      className="flex h-full flex-col overflow-hidden"
      style={{ background: "#0d0a20" }}
    >
      <div ref={containerRef} className="min-h-0 flex-1" />
      <div className="flex shrink-0 items-center justify-center gap-4 py-2">
        {["Vertex", "Faces", "Maillage"].map((l, i) => (
          <div key={l} className="flex flex-col items-center">
            <span className="text-[12px] font-bold text-violet-300">
              {[24680, 49352, 24676][i]!.toLocaleString()}
            </span>
            <span className="text-[10px] text-violet-500/70">{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function VideoPreview() {
  const [playing, setPlaying] = React.useState(false);
  return (
    <div
      className="flex h-full items-center justify-center"
      style={{ background: "#0a0f1a" }}
    >
      <div
        className="relative overflow-hidden rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.5)]"
        style={{ width: 380, height: 240, maxWidth: "95%" }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "linear-gradient(135deg, #0f2942 0%, #1a3a5c 100%)",
          }}
        />
        {!playing ? (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-all hover:bg-white/30 hover:scale-105">
              <I.Play size={34} className="ml-1.5 text-white" />
            </div>
          </button>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <div className="h-2.5 w-2.5 animate-bounce rounded-full bg-cyan-400" />
            <span className="text-[13px] font-semibold text-cyan-300">
              Lecture en cours…
            </span>
            <span className="text-[11px] text-cyan-400/60">(démo)</span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/80 px-4 py-3">
          <div className="mb-1.5 h-1 w-full overflow-hidden rounded-full bg-white/20">
            <div
              className={cn(
                "h-full rounded-full bg-cyan-400 transition-all duration-500",
                playing ? "w-1/3" : "w-0",
              )}
            />
          </div>
          <div className="flex items-center justify-between text-[10px] text-white/60">
            <span>{playing ? "0:24" : "0:00"}</span>
            <span className="font-semibold text-white">
              Évolution traitement — 6 mois
            </span>
            <span>1:38</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PdfPreview({ name }: { name: string }) {
  return (
    <div
      className="flex h-full items-center justify-center"
      style={{ background: "#f8fafc" }}
    >
      <div
        className="overflow-hidden rounded-xl border border-sf-border shadow-[0_12px_40px_rgba(0,0,0,0.12)]"
        style={{ width: 260, height: 340 }}
      >
        <div className="flex h-14 items-center justify-between bg-red-600 px-5">
          <span className="text-[15px] font-bold text-white">PDF</span>
          <I.FileText size={20} className="text-white/70" />
        </div>
        <div className="bg-white px-5 py-5">
          <div className="mb-4 text-[13px] font-bold leading-snug text-slate-800">
            {name.replace(/-/g, " ")}
          </div>
          {[0.9, 0.7, 0.85, 0.6, 0.78, 0.45, 0.68, 0.52, 0.8].map((w, i) => (
            <div
              key={i}
              className="mb-2 h-2 rounded-full bg-slate-200"
              style={{ width: `${w * 100}%` }}
            />
          ))}
          <div className="mt-5 h-14 w-full rounded border border-slate-200 bg-slate-50" />
          <div className="mt-4">
            {[0.8, 0.6, 0.72].map((w, i) => (
              <div
                key={i}
                className="mb-2 h-2 rounded-full bg-slate-200"
                style={{ width: `${w * 100}%` }}
              />
            ))}
          </div>
        </div>
        <div className="border-t border-sf-border bg-sf-surface-alt px-5 py-2 text-[10px] text-sf-text-muted">
          Cabinet Ligam · 2024
        </div>
      </div>
    </div>
  );
}

// ── Filters / sort constants ─────────────────────────────────────────────────

const FILTER_LABELS: { key: MediaFileType | "all"; label: string }[] = [
  { key: "all", label: "Tous" },
  { key: "jpeg", label: "Photos" },
  { key: "png", label: "Radios" },
  { key: "stl", label: "3D" },
  { key: "mp4", label: "Vidéos" },
  { key: "pdf", label: "PDF" },
];

const SORT_OPTIONS = [
  { value: "type", label: "Type" },
  { value: "date", label: "Date" },
  { value: "name", label: "Nom" },
];

const SIZE_OPTIONS: {
  key: "sm" | "md" | "lg";
  label: string;
  minPx: number;
}[] = [
  { key: "sm", label: "S", minPx: 80 },
  { key: "md", label: "M", minPx: 112 },
  { key: "lg", label: "L", minPx: 152 },
];

type SortKey = "type" | "date" | "name";

function sortFiles(files: MediaFile[], sort: SortKey): MediaFile[] {
  const order: Record<string, number> = {
    png: 0,
    jpeg: 1,
    stl: 2,
    mp4: 3,
    pdf: 4,
  };
  return [...files].sort((a, b) => {
    if (sort === "type") return (order[a.type] ?? 9) - (order[b.type] ?? 9);
    if (sort === "date") return b.date.localeCompare(a.date);
    return a.name.localeCompare(b.name);
  });
}

// ── Context menu ─────────────────────────────────────────────────────────────

interface ContextMenuState {
  x: number;
  y: number;
  fileId: string;
}

function ContextMenu({
  menu,
  selCount,
  onClose,
  onDownload,
  onDelete,
  onRename,
  onOpenPreview,
}: {
  menu: ContextMenuState;
  selCount: number;
  onClose: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onRename: () => void;
  onOpenPreview: () => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const target = selCount > 1 ? `${selCount} fichiers` : "ce fichier";

  const items: {
    icon: React.ReactNode;
    label: string;
    action: () => void;
    danger?: boolean;
    separator?: boolean;
  }[] = [
    { icon: <I.Eye size={13} />, label: "Aperçu", action: onOpenPreview },
    {
      icon: <I.Download size={13} />,
      label: `Télécharger ${target}`,
      action: onDownload,
      separator: true,
    },
    { icon: <I.Edit size={13} />, label: "Renommer", action: onRename },
    {
      icon: <I.Trash size={13} />,
      label: `Supprimer ${target}`,
      action: onDelete,
      danger: true,
    },
  ];

  return (
    <div
      ref={ref}
      className="fixed z-50 min-w-48 overflow-hidden rounded-lg border border-sf-border bg-white shadow-[0_8px_32px_rgba(0,0,0,0.18)]"
      style={{ left: menu.x, top: menu.y }}
    >
      {items.map((item, i) => (
        <React.Fragment key={i}>
          {item.separator && <div className="my-1 border-t border-sf-border" />}
          <button
            type="button"
            onClick={() => {
              item.action();
              onClose();
            }}
            className={cn(
              "flex w-full items-center gap-2.5 px-3.5 py-2 text-[12.5px] transition-colors hover:bg-sf-surface-alt",
              item.danger ? "text-red-600 hover:bg-red-50" : "text-sf-text",
            )}
          >
            {item.icon}
            {item.label}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function MediaModule({ patient }: { patient: Patient }) {
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = React.useState<string | null>(null);
  const [previewId, setPreviewId] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<MediaFileType | "all">("all");
  const [sort, setSort] = React.useState<SortKey>("type");
  const [thumbSize, setThumbSize] = React.useState<"sm" | "md" | "lg">("md");
  const [contextMenu, setContextMenu] = React.useState<ContextMenuState | null>(
    null,
  );
  const [renaming, setRenaming] = React.useState<string | null>(null);
  const [renameValue, setRenameValue] = React.useState("");
  const [deletedIds, setDeletedIds] = React.useState<Set<string>>(new Set());
  const galleryRef = React.useRef<HTMLDivElement>(null);
  const lastClickedId = React.useRef<string | null>(null);

  const allFiles = React.useMemo(
    () => getPatientFiles(patient.uuid),
    [patient.uuid],
  );
  const activeFiles = React.useMemo(
    () => allFiles.filter((f) => !deletedIds.has(f.id)),
    [allFiles, deletedIds],
  );

  const countByType = React.useMemo(() => {
    const counts: Partial<Record<MediaFileType, number>> = {};
    for (const f of activeFiles) counts[f.type] = (counts[f.type] ?? 0) + 1;
    return counts;
  }, [activeFiles]);

  const visibleFiles = React.useMemo(() => {
    const filtered =
      filter === "all"
        ? activeFiles
        : activeFiles.filter((f) => f.type === filter);
    return sortFiles(filtered, sort);
  }, [activeFiles, filter, sort]);

  const previewFile = React.useMemo(
    () =>
      activeFiles.find(
        (f) =>
          f.id ===
          (previewId ?? (selectedIds.size === 1 ? [...selectedIds][0] : null)),
      ) ?? null,
    [activeFiles, previewId, selectedIds],
  );

  const minPx = SIZE_OPTIONS.find((s) => s.key === thumbSize)!.minPx;

  const filterOptions = React.useMemo(
    () =>
      FILTER_LABELS.filter(
        ({ key }) =>
          key === "all" || (countByType[key as MediaFileType] ?? 0) > 0,
      ).map(({ key, label }) => ({
        value: key,
        label,
        badge:
          key === "all"
            ? activeFiles.length
            : (countByType[key as MediaFileType] ?? 0),
      })),
    [activeFiles.length, countByType],
  );

  React.useEffect(() => {
    if (!filterOptions.some((o) => o.value === filter)) setFilter("all");
  }, [filter, filterOptions]);

  // Deselect removed files
  React.useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => !deletedIds.has(id)));
      return next.size !== prev.size ? next : prev;
    });
  }, [deletedIds]);

  // ── Selection logic ──────────────────────────────────────────────────────

  const handlePointerDown = React.useCallback(
    (file: MediaFile, e: React.PointerEvent) => {
      e.preventDefault();
      const id = file.id;
      setFocusedId(id);

      if (e.ctrlKey || e.metaKey) {
        // Ctrl+click: toggle
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
      } else if (e.shiftKey && lastClickedId.current) {
        // Shift+click: range selection
        const ids = visibleFiles.map((f) => f.id);
        const a = ids.indexOf(lastClickedId.current);
        const b = ids.indexOf(id);
        const [lo, hi] = a < b ? [a, b] : [b, a];
        setSelectedIds(new Set(ids.slice(lo, hi + 1)));
      } else {
        // Plain click: select only this
        setSelectedIds(new Set([id]));
      }
      lastClickedId.current = id;
    },
    [visibleFiles],
  );

  const handleDoubleClick = React.useCallback((id: string) => {
    setPreviewId(id);
  }, []);

  // ── Context menu ─────────────────────────────────────────────────────────

  const handleContextMenu = React.useCallback(
    (e: React.MouseEvent, file: MediaFile) => {
      e.preventDefault();
      if (!selectedIds.has(file.id)) setSelectedIds(new Set([file.id]));
      setContextMenu({ x: e.clientX, y: e.clientY, fileId: file.id });
    },
    [selectedIds],
  );

  // ── Keyboard shortcuts ───────────────────────────────────────────────────

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;

      if ((e.ctrlKey || e.metaKey) && e.key === "a") {
        e.preventDefault();
        setSelectedIds(new Set(visibleFiles.map((f) => f.id)));
      }
      if (e.key === "Escape") {
        setSelectedIds(new Set());
        setContextMenu(null);
        setPreviewId(null);
      }
      if (e.key === "Delete" && selectedIds.size > 0) {
        handleDelete();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [visibleFiles, selectedIds]);

  // ── Operations ───────────────────────────────────────────────────────────

  const handleDelete = React.useCallback(() => {
    setDeletedIds((prev) => new Set([...prev, ...selectedIds]));
    setSelectedIds(new Set());
    setContextMenu(null);
  }, [selectedIds]);

  const handleDownload = React.useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleRename = React.useCallback(() => {
    const id = contextMenu?.fileId ?? [...selectedIds][0];
    if (!id) return;
    const file = activeFiles.find((f) => f.id === id);
    if (file) {
      setRenaming(id);
      setRenameValue(file.name);
    }
    setContextMenu(null);
  }, [contextMenu, selectedIds, activeFiles]);

  return (
    <div
      className="flex h-full flex-col-reverse overflow-hidden lg:flex-row"
      onContextMenu={(e) => {
        if (e.target === galleryRef.current) {
          e.preventDefault();
          setContextMenu(null);
        }
      }}
    >
      {/* ── Gallery — en bas sur mobile (flex-col-reverse), à gauche sur lg ── */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-t border-sf-border bg-sf-surface lg:border-t-0 lg:border-r">
        {/* Toolbar — une seule ligne */}
        <div className="flex h-9 shrink-0 items-center gap-1 border-b border-sf-border px-1.5">
          {/* Filtres types */}
          <div className="min-w-0 flex-1 overflow-hidden">
            <OptionStepper
              ariaLabel="Type de fichier"
              value={filter}
              onChange={(v) => {
                setFilter(v as MediaFileType | "all");
                setSelectedIds(new Set());
              }}
              options={filterOptions}
            />
          </div>

          {/* Séparateur */}
          <div className="h-5 w-px shrink-0 bg-sf-border" />

          {selectedIds.size > 0 ? (
            /* Mode sélection */
            <>
              <span className="shrink-0 rounded bg-sf-primary/10 px-1.5 py-0.5 text-[10.5px] font-semibold text-sf-primary">
                {selectedIds.size}
              </span>
              <button
                type="button"
                onClick={handleDownload}
                title="Télécharger"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-sf-border bg-white text-sf-text-muted hover:bg-sf-surface-alt"
              >
                <I.Download size={12} />
              </button>
              <button
                type="button"
                onClick={handleRename}
                title="Renommer"
                disabled={selectedIds.size > 1}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-sf-border bg-white text-sf-text-muted hover:bg-sf-surface-alt disabled:opacity-30"
              >
                <I.Edit size={12} />
              </button>
              <button
                type="button"
                onClick={handleDelete}
                title="Supprimer (Suppr)"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
              >
                <I.Trash size={12} />
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                title="Désélectionner (Échap)"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-sf-border bg-white text-sf-text-muted hover:bg-sf-surface-alt"
              >
                <I.X size={12} />
              </button>
            </>
          ) : (
            /* Mode normal */
            <>
              <Select
                value={sort}
                onChange={(v) => setSort(v as SortKey)}
                options={SORT_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
                className="w-32"
              />
              <OptionStepper
                ariaLabel="Taille"
                value={thumbSize}
                onChange={(v) => setThumbSize(v as "sm" | "md" | "lg")}
                options={SIZE_OPTIONS.map((s) => ({
                  value: s.key,
                  label: s.label,
                }))}
              />
              <button
                type="button"
                title="Importer des fichiers"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-sf-border bg-white text-sf-text-muted hover:bg-sf-surface-alt"
              >
                <I.Upload size={12} />
              </button>
            </>
          )}
        </div>

        {/* Grid */}
        <div
          ref={galleryRef}
          role="listbox"
          aria-multiselectable
          aria-label="Fichiers du patient"
          className="min-h-0 flex-1 overflow-y-auto p-2"
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(${minPx}px, 1fr))`,
            gap: 6,
            alignContent: "start",
          }}
          onClick={(e) => {
            if (e.target === galleryRef.current) setSelectedIds(new Set());
          }}
        >
          {visibleFiles.map((file) => (
            <div
              key={file.id}
              onContextMenu={(e) => handleContextMenu(e, file)}
            >
              {renaming === file.id ? (
                <div className="flex flex-col overflow-hidden rounded-lg border-2 border-sf-primary bg-white">
                  <div
                    className="w-full shrink-0 bg-cover bg-center bg-no-repeat opacity-50"
                    style={{
                      backgroundImage: `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(getThumbSvg(file))}")`,
                      aspectRatio: "4/3",
                    }}
                  />
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setRenaming(null);
                      }
                      if (e.key === "Escape") {
                        setRenaming(null);
                      }
                    }}
                    onBlur={() => setRenaming(null)}
                    className="w-full border-t border-sf-border bg-white px-1.5 py-1 text-[10.5px] outline-none focus:bg-sf-primary-50"
                  />
                </div>
              ) : (
                <FileThumbnail
                  file={file}
                  selected={selectedIds.has(file.id)}
                  focused={focusedId === file.id}
                  onPointerDown={(e) => handlePointerDown(file, e)}
                  onDoubleClick={() => handleDoubleClick(file.id)}
                />
              )}
            </div>
          ))}
          {visibleFiles.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-sf-text-muted">
              <I.FolderOpen size={28} className="mb-2 opacity-40" />
              <span className="text-[12px]">Aucun fichier</span>
            </div>
          )}
        </div>

        {/* Status bar */}
        <div className="shrink-0 flex items-center justify-between border-t border-sf-border bg-sf-surface-alt px-3 py-1">
          <span className="text-[10.5px] text-sf-text-muted">
            {visibleFiles.length} fichier{visibleFiles.length > 1 ? "s" : ""}
            {selectedIds.size > 0 && (
              <>
                {" "}
                ·{" "}
                <strong className="text-sf-primary">
                  {selectedIds.size} sélectionné
                  {selectedIds.size > 1 ? "s" : ""}
                </strong>
              </>
            )}
          </span>
          <span className="text-[10px] text-sf-text-muted/60 hidden sm:inline">
            Ctrl+A · Suppr · Échap
          </span>
        </div>
      </div>

      {/* ── Preview — en haut sur mobile, à droite sur lg ── */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {!previewFile ? (
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-sf-primary-50 text-sf-primary">
              <I.Image size={26} />
            </div>
            <p className="text-[12.5px] text-sf-text-muted">
              {selectedIds.size > 1
                ? `${selectedIds.size} fichiers sélectionnés`
                : "Double-cliquez pour afficher l'aperçu"}
            </p>
            {selectedIds.size > 1 && (
              <div className="flex flex-col items-center gap-1">
                {[...selectedIds].slice(0, 4).map((id) => {
                  const f = activeFiles.find((x) => x.id === id);
                  return f ? (
                    <span
                      key={id}
                      className="text-[11px] text-sf-text-secondary truncate max-w-48"
                    >
                      {f.name}
                    </span>
                  ) : null;
                })}
                {selectedIds.size > 4 && (
                  <span className="text-[11px] text-sf-text-muted">
                    +{selectedIds.size - 4} autres
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex shrink-0 items-center gap-3 border-b border-sf-border bg-white px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-[14px] font-bold text-sf-text">
                    {previewFile.name}
                  </span>
                  <span
                    className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase"
                    style={{
                      background: TYPE_META[previewFile.type].color,
                      color: TYPE_META[previewFile.type].textColor,
                    }}
                  >
                    {TYPE_META[previewFile.type].label}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] text-sf-text-muted">
                  {formatFileSize(previewFile.size)} ·{" "}
                  {formatFileDate(previewFile.date)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  title="Télécharger"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-sf-border bg-white text-sf-text-muted hover:bg-sf-surface-alt"
                >
                  <I.Download size={14} />
                </button>
                {previewId && (
                  <button
                    type="button"
                    title="Fermer l'aperçu"
                    onClick={() => setPreviewId(null)}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-sf-border bg-white text-sf-text-muted hover:bg-sf-surface-alt"
                  >
                    <I.X size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden">
              {previewFile.type === "jpeg" && (
                <PhotoPreview fileId={previewFile.id} />
              )}
              {previewFile.type === "png" && (
                <XrayPreview fileId={previewFile.id} />
              )}
              {previewFile.type === "stl" && (
                <StlPreview fileId={previewFile.id} />
              )}
              {previewFile.type === "mp4" && <VideoPreview />}
              {previewFile.type === "pdf" && (
                <PdfPreview name={previewFile.name} />
              )}
            </div>
          </>
        )}
      </div>

      {/* Context menu portal */}
      {contextMenu && (
        <ContextMenu
          menu={contextMenu}
          selCount={selectedIds.size}
          onClose={() => setContextMenu(null)}
          onDownload={handleDownload}
          onDelete={handleDelete}
          onRename={handleRename}
          onOpenPreview={() => {
            setPreviewId(contextMenu.fileId);
            setContextMenu(null);
          }}
        />
      )}
    </div>
  );
}
